import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useFetchAllConcerns from "../lib/fetchconcern";
import useCloseConcern from "../lib/markConcernAsSolved";
import Spinner from "../../utils/spinner";
import { CiSearch } from "react-icons/ci";
import "./All_customer_concern.css";

export default function Allcustomerconcern() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [closeConcernOverlay, setCloseConcernOverlay] = useState(false);
  const [selectedConcernId, setSelectedConcernId] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [closeSuccess, setCloseSuccess] = useState(false);

  const {
    closeConcern,
    loading: closeLoading,
    error: closeError,
  } = useCloseConcern();

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useFetchAllConcerns();

  const observerRef = useRef();
  const lastConcernRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage]
  );

  const filteredConcerns = data?.pages
    .flatMap((page) => page.concerns)
    .filter((concern) =>
      concern?.trackingID?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleViewConcernDetails = (concern) => {
    navigate(`/concernsdetails/${concern._id}`, { state: concern });
  };

  const handleMarkAsSolved = async () => {
    try {
      await closeConcern(selectedConcernId, responseText);
      setCloseSuccess(true);

      setTimeout(() => {
        setCloseSuccess(false);
        setCloseConcernOverlay(false);
        setSelectedConcernId(null);
        setResponseText("");
      }, 2000);
    } catch (err) {
      console.error("Failed to close concern:", err.message);
    }
  };

  return (
    <>
      {closeConcernOverlay && (
        <div className="close-concern-overlay">
          <div className="card">
            <p>Are you sure you want to mark this Concern as Closed?</p>

            <textarea
              placeholder="Write your response here..."
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              rows={4}
              style={{ width: "100%", marginTop: "1rem" }}
              required
            />

            <div className="btn">
              <span>
                <button
                  onClick={() => {
                    setCloseConcernOverlay(false);
                    setSelectedConcernId(null);
                    setResponseText("");
                    setCloseSuccess(false);
                  }}
                >
                  No
                </button>
                <button
                  onClick={handleMarkAsSolved}
                  disabled={closeLoading || responseText.trim() === ""}
                >
                  {closeLoading ? "Solving..." : "Yes"}
                </button>
              </span>

              {closeSuccess && (
                <p
                  style={{
                    fontFamily: "Montserrat",
                    color: "green",
                    fontWeight: "bold",
                  }}
                >
                  Concern closed successfully!
                </p>
              )}
              {closeError && (
                <p style={{ fontFamily: "Montserrat", color: "red" }}>
                  {closeError}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="raised-concern">
        <div className="headers">
          <p>Business Customer Concern</p>
          <span>
            <CiSearch style={{ fontSize: "24px" }} />
            <input
              type="text"
              placeholder="Search concern by Tracking Num"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </span>
        </div>

        <div className="customer-concerns">
          {isLoading && <Spinner />}
          {isError && (
            <p style={{ fontFamily: "Montserrat" }}>Failed to load concerns.</p>
          )}
          {!isLoading && !isError && (
            <table>
              <thead>
                <tr>
                  <th>Ticket Code</th>
                  <th>Tracking Number</th>
                  <th>Customer Email</th>
                  <th>Subject</th>
                  <th>Concern</th>
                  <th>Status</th>
                  <th>Date Submitted</th>
                  <th>Details</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredConcerns?.map((concern, i) => {
                  const isLast = i === filteredConcerns.length - 1;
                  return (
                    <tr key={concern._id} ref={isLast ? lastConcernRef : null}>
                      <td>{concern?.ticketCode}</td>
                      <td>{concern?.trackingID}</td>
                      <td>{concern?.raisedBy?.email}</td>
                      <td>{concern.subject}</td>
                      <td>
                        {concern?.message?.split(" ").slice(0, 4).join(" ")}...
                      </td>
                      <td>{concern.status}</td>
                      <td>
                        {new Date(concern.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <button
                          onClick={() => handleViewConcernDetails(concern)}
                        >
                          View
                        </button>
                      </td>
                      <td>
                        <button
                          onClick={() => {
                            if (concern.status !== "Closed") {
                              setSelectedConcernId(concern._id);
                              setCloseConcernOverlay(true);
                            }
                          }}
                          disabled={concern.status === "Closed"}
                          style={{
                            cursor:
                              concern.status === "Closed"
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          Marked as Solved
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {isFetchingNextPage && <Spinner />}
      </div>
    </>
  );
}
